import { useState, useTransition } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { BrandCard } from './buds-header';
import { callApi, ShopifyAppApiError } from '@/lib/api';
import { automaticSyncBlockReason } from './sync-ui-state';

interface Props {
  initialEnabled: boolean;
  initialSyncCompleted: boolean;
  reconnectCatchUpRequired: boolean;
  lastAutoSyncedAt: string | null;
  lastAutoSyncError: string | null;
  onChange: (next: boolean) => void;
  onReconnectRequired: (message?: string) => void;
}

export function SyncSettingsCard({
  initialEnabled,
  initialSyncCompleted,
  reconnectCatchUpRequired,
  lastAutoSyncedAt,
  lastAutoSyncError,
  onChange,
  onReconnectRequired,
}: Props) {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  async function toggle() {
    setError(null);
    const next = !enabled;
    setEnabled(next);
    try {
      const body = await callApi<{ autoSyncEnabled: boolean }>(
        '/v1/shopify-app/settings',
        { method: 'PATCH', body: { autoSyncEnabled: next } },
      );
      setEnabled(body.autoSyncEnabled);
      onChange(body.autoSyncEnabled);
    } catch (err) {
      setEnabled(!next);
      const e = err as ShopifyAppApiError;
      if (e.code === 'NOT_CONNECTED') {
        onReconnectRequired(e.message);
        return;
      }
      setError(`Could not update automatic sync: ${e.message}`);
    }
  }

  const blockReason = automaticSyncBlockReason(
    initialSyncCompleted,
    reconnectCatchUpRequired,
  );
  const blocked = blockReason !== null;

  return (
    <BrandCard title="Automatic sync">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-buds-dark">
            Keep Buds rewards up to date
          </div>
          <div className="mt-1 text-xs text-buds-dark/60">
            When enabled, Shopify product changes automatically update their
            managed Buds rewards.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-label="Automatic product sync"
          aria-checked={enabled}
          onClick={() => startTransition(toggle)}
          disabled={pending || blocked}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition focus:outline-none focus-visible:ring-2 focus-visible:ring-buds-green disabled:cursor-not-allowed disabled:opacity-60 ${enabled ? 'bg-buds-green' : 'bg-buds-dark/20'}`}
        >
          <span
            aria-hidden="true"
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${enabled ? 'translate-x-5' : 'translate-x-0'}`}
          />
        </button>
      </div>
      {blockReason === 'FIRST_SYNC_REQUIRED' && (
        <div className="mt-3 rounded-md border border-buds-sky/60 bg-buds-sky/10 px-3 py-2 text-xs text-buds-dark">
          Run your first manual product sync above. Automatic sync becomes
          available after that run completes successfully.
        </div>
      )}
      {blockReason === 'RECONNECT_CATCH_UP_REQUIRED' && (
        <div className="mt-3 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Shopify was reconnected. Run a manual product sync above to catch up,
          then re-enable automatic sync.
        </div>
      )}
      {lastAutoSyncError && (
        <div className="mt-3 rounded-md border border-red-400/60 bg-red-50 px-3 py-2 text-xs text-red-900">
          <span className="font-medium">Latest automatic update failed.</span>{' '}
          {lastAutoSyncError} Run a manual sync to catch up; future Shopify
          changes will be retried automatically while automatic sync is on.
        </div>
      )}
      {!lastAutoSyncError && lastAutoSyncedAt && (
        <p className="mt-3 text-xs text-buds-dark/60">
          Last automatic update{' '}
          {formatDistanceToNowStrict(new Date(lastAutoSyncedAt), {
            addSuffix: true,
          })}
          .
        </p>
      )}
      {error && (
        <div className="mt-3 rounded-md border border-red-400/60 bg-red-50 px-3 py-2 text-xs text-red-900">
          {error}
        </div>
      )}
    </BrandCard>
  );
}
